'use client';

import type { HTMLAttributes, ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { clsx } from 'clsx';
import type { TypographyProps } from '../typography';
import { TypographyProvider, TypographyProviderReset } from '../typography/context';
import styles from './tooltip.module.scss';

type TooltipSize = 'small' | 'large';

const baseTypography = {
  tone: 'background',
} satisfies TypographyProps;

const typographyBySize: Record<TooltipSize, TypographyProps> = {
  small: {
    ...baseTypography,
    size: '0.75rem',
    lineHeight: '1rem',
  },
  large: {
    ...baseTypography,
    size: '0.875rem',
    lineHeight: '1.125rem',
  },
};

export interface TooltipProps extends HTMLAttributes<HTMLDivElement> {
  activator: ReactNode;
  activatorIsPrimitive?: boolean;
  align?: 'start' | 'center' | 'end';
  alignOffset?: number;
  children: ReactNode;
  delayDuration?: number;
  maxWidth?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  size?: TooltipSize;
  withArrow?: boolean;
}

export const TooltipActivator = TooltipPrimitive.Trigger;

export function Tooltip({
  activator,
  activatorIsPrimitive = false,
  children,
  className,
  delayDuration = 0,
  maxWidth,
  open,
  onOpenChange,
  sideOffset = 2,
  size = 'small',
  style,
  withArrow = true,
  ...rest
}: TooltipProps) {
  return (
    <TypographyProviderReset>
      <TooltipPrimitive.Provider>
        <TooltipPrimitive.Root delayDuration={delayDuration} onOpenChange={onOpenChange} open={open}>
          {activatorIsPrimitive ? activator : <TooltipActivator asChild>{activator}</TooltipActivator>}
          <TooltipPrimitive.TooltipPortal>
            <TooltipPrimitive.Content
              className={clsx(styles.tooltip, styles[`size-${size}`], className)}
              sideOffset={sideOffset}
              style={{
                ...style,
                maxWidth,
              }}
              {...rest}
            >
              <TypographyProvider value={typographyBySize[size]}>{children}</TypographyProvider>
              {withArrow ? <TooltipPrimitive.Arrow className={styles.arrow} height={6} width={12} /> : null}
            </TooltipPrimitive.Content>
          </TooltipPrimitive.TooltipPortal>
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    </TypographyProviderReset>
  );
}
